// frontend/src/components/VideoSlot.tsx
// A single video module on the Video Analysis page.
// Handles empty state (search + select) and loaded state (video + controls).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  GetApp as DownloadIcon,
  SwapHoriz as ChangeIcon,
  VideoFile as VideoFileIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  getPresignedViewUrl,
  getPresignedDownloadUrl,
  searchAllFiles,
  FileMetadata,
  getFileName,
} from '../services/api';
import { getDisplayFileName } from '../services/FileUtils';
import { SlotState } from '../services/analysisSession';

const SPEED_OPTIONS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 10, 12];
const rocheBlue = '#0B41CD';

interface VideoSlotProps {
  slot: SlotState;
  globalSpeed: number;
  syncEnabled: boolean;
  onRemove: (id: string) => void;
  onVideoChange: (id: string, key: string | null) => void;
  onSpeedOverride: (id: string, speed: number | null) => void;
  registerVideo: (id: string, el: HTMLVideoElement | null) => void;
  onSyncEvent: (type: 'play' | 'pause' | 'seek', time?: number) => void;
}

const VideoSlot: React.FC<VideoSlotProps> = ({
  slot,
  globalSpeed,
  syncEnabled,
  onRemove,
  onVideoChange,
  onSpeedOverride,
  registerVideo,
  onSyncEvent,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FileMetadata[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Effective speed: per-slot override takes precedence over global
  const effectiveSpeed = slot.speedOverride ?? globalSpeed;

  // Register/unregister video element with parent for sync
  const setVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      registerVideo(slot.id, el);
    },
    [slot.id, registerVideo]
  );

  // Fetch presigned URL when a key is set
  useEffect(() => {
    if (!slot.key) {
      setVideoUrl(null);
      return;
    }
    let cancelled = false;
    setLoadingUrl(true);
    setUrlError(null);
    getPresignedViewUrl(slot.key, 7200)
      .then((res) => {
        if (!cancelled) setVideoUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setUrlError('Failed to load video URL. Try again.');
      })
      .finally(() => {
        if (!cancelled) setLoadingUrl(false);
      });
    return () => { cancelled = true; };
  }, [slot.key]);

  // Apply speed whenever effectiveSpeed or video element changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = effectiveSpeed;
    }
  }, [effectiveSpeed, videoUrl]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchAllFiles(searchQuery.trim());
        const mp4s = (res.files ?? []).filter((f) =>
          f.key.toLowerCase().endsWith('.mp4')
        );
        setSearchResults(mp4s.slice(0, 30));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  const handleSelectVideo = (key: string) => {
    setSearchQuery('');
    setSearchResults([]);
    onVideoChange(slot.id, key);
  };

  const handleChangeVideo = () => {
    onVideoChange(slot.id, null);
    setVideoUrl(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleDownload = async () => {
    if (!slot.key) return;
    try {
      const res = await getPresignedDownloadUrl(slot.key);
      const link = document.createElement('a');
      link.href = res.url;
      link.download = res.filename || getFileName(slot.key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // Silently fail — user can retry
    }
  };

  const handleFullscreen = () => {
    videoRef.current?.requestFullscreen().catch(() => {});
  };

  const handleSpeedChange = (_: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value === null) return;
    onSpeedOverride(slot.id, value === globalSpeed ? null : value);
  };

  const handleResetSpeed = () => {
    onSpeedOverride(slot.id, null);
  };

  // Sync event handlers — only fire when sync is enabled
  const handlePlay = () => {
    if (syncEnabled) onSyncEvent('play');
  };
  const handlePause = () => {
    if (syncEnabled) onSyncEvent('pause');
  };
  const handleSeeked = () => {
    if (syncEnabled && videoRef.current) {
      onSyncEvent('seek', videoRef.current.currentTime);
    }
  };

  const displayName = slot.key ? getDisplayFileName(slot.key) : null;

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      {/* Slot header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          gap: 1,
          minHeight: 40,
        }}
      >
        <VideoFileIcon sx={{ color: rocheBlue, fontSize: 18, flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            color: displayName ? 'text.primary' : 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName ?? 'Empty slot'}
        </Typography>
        {slot.key && (
          <>
            <Tooltip title="Change video">
              <IconButton size="small" onClick={handleChangeVideo}>
                <ChangeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size="small" onClick={handleFullscreen}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Remove slot">
          <IconButton size="small" onClick={() => onRemove(slot.id)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Empty state — search */}
        {!slot.key && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search for an MP4…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />,
              }}
            />
            {searching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!searching && searchQuery.trim() && searchResults.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No videos found.
              </Typography>
            )}
            {searchResults.length > 0 && (
              <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                <List dense disablePadding>
                  {searchResults.map((file) => (
                    <ListItem key={file.key} disablePadding>
                      <ListItemButton onClick={() => handleSelectVideo(file.key)}>
                        <ListItemText
                          primary={getDisplayFileName(file.key)}
                          secondary={
                            file.lastModified
                              ? new Date(file.lastModified).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : undefined
                          }
                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            {!searchQuery.trim() && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  gap: 1,
                  py: 4,
                }}
              >
                <VideoFileIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">Search for a video above to load it here</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Loading URL */}
        {slot.key && loadingUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* URL error */}
        {slot.key && urlError && !loadingUrl && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography variant="body2" color="error">{urlError}</Typography>
            <Button size="small" onClick={handleChangeVideo}>Choose a different video</Button>
          </Box>
        )}

        {/* Loaded video */}
        {slot.key && videoUrl && !loadingUrl && (
          <>
            <Box sx={{ backgroundColor: '#000', lineHeight: 0 }}>
              <video
                ref={setVideoRef}
                src={videoUrl}
                controls
                playsInline
                preload="metadata"
                style={{ width: '100%', display: 'block', maxHeight: '60vh' }}
                onPlay={handlePlay}
                onPause={handlePause}
                onSeeked={handleSeeked}
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    videoRef.current.playbackRate = effectiveSpeed;
                  }
                }}
              />
            </Box>

            {/* Per-slot speed controls */}
            <Divider />
            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                Speed:
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={effectiveSpeed}
                onChange={handleSpeedChange}
                sx={{ flexWrap: 'wrap', gap: 0.25 }}
              >
                {SPEED_OPTIONS.map((s) => (
                  <ToggleButton
                    key={s}
                    value={s}
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.7rem',
                      minWidth: 36,
                      border: '1px solid rgba(0,0,0,0.15) !important',
                      '&.Mui-selected': {
                        backgroundColor: rocheBlue,
                        color: '#fff',
                        '&:hover': { backgroundColor: rocheBlue },
                      },
                    }}
                  >
                    {s}×
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {slot.speedOverride !== null && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ fontSize: '0.7rem', py: 0, minWidth: 0, color: 'text.secondary' }}
                  onClick={handleResetSpeed}
                >
                  reset
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default VideoSlot;
